import { apiConstants } from "../../../constants/apiConstants"
import { authService } from "../../../services/authService"
import { liveServiceV2 } from "../../../services/liveServiceV2"
import { studentsService } from "../../../services/studentsService"

/**
 * Une réponse ne dit la liste d'activités d'un élève que si elle est partie après la dernière écriture
 * de cette liste.
 *
 * Les réducteurs de `currentUser` retirent de la liste tout ce qu'une réponse ne porte pas : c'est ainsi
 * qu'une activité retirée s'en va. Sans ordre entre les réponses, le relevé de la séance parti avant un
 * ajout l'effaçait à son arrivée, et un travail rendu repassait à faire, jusqu'au relevé suivant (60 s).
 *
 * Une lecture vaut pour le moment de son départ. Toute autre réponse qui change une liste vaut pour le
 * moment où elle revient : un relevé parti pendant une création a pu lire la base avant elle. Écartée,
 * une réponse garde tout le reste, et le relevé suivant remet la liste au clair. Une écriture datée après
 * la réponse ne se lit que si l'horloge de l'appareil a reculé : elle n'écarte rien, sans quoi la liste
 * resterait figée le temps du recul.
 *
 * Les listes du compte lui-même (l'élève connecté, `object.todo_activities` et `object.activities`)
 * suivent la même règle, sous leur propre clé : sa relecture (`GET /api/user`) est une lecture, toute
 * autre réponse qui les change (une activité ajoutée par l'app) une écriture. Le live de l'élève y lit
 * qu'une activité absente l'est vraiment, et non d'une réponse partie avant elle.
 */
const success = (service) => service.name + apiConstants._SUCCESS

const SESSION_READ = success(liveServiceV2.getLiveSessionV2)
const STUDENT_READ = success(studentsService.getStudent)
const ACCOUNT_READ = success(authService.getUser)
/** La clé des listes du compte lui-même dans `listsWrittenAt`, à côté de celles des élèves (leurs ids). */
const ACCOUNT = "account"

function readStudentIds(action) {
    let ids = null
    if (action.type === SESSION_READ) ids = (action.data?.live_session?.users ?? []).map((user) => user?.id)
    if (action.type === STUDENT_READ) ids = [action.data?.id]
    return ids?.filter((id) => id != null) ?? null
}

const copyIn = (copies, id) => copies?.find((copy) => copy?.id === id)

/** Les copies d'un élève : la liste `students`, et chaque séance. Deux séances peuvent différer. */
function copyGroups(state) {
    return [
        { key: "students", copies: state.object?.students },
        ...(state.object?.live_sessions ?? []).map((session) => ({ key: session?.id, copies: session?.users })),
    ]
}

function keepLists(copies, previousCopies, staleIds) {
    if (!copies?.some((copy) => staleIds.has(copy?.id))) return copies
    return copies.map((copy) => {
        const before = staleIds.has(copy?.id) ? copyIn(previousCopies, copy.id) : null
        return carriesLists(before) ? withListsOf(copy, before) : copy
    })
}

function restoreLists(previous, next, staleIds) {
    const previousSession = (id) => previous.object?.live_sessions?.find((session) => session?.id === id)
    return {
        ...next,
        object: {
            ...next.object,
            students: keepLists(next.object?.students, previous.object?.students, staleIds),
            live_sessions: next.object?.live_sessions?.map((session) => {
                const users = keepLists(session?.users, previousSession(session?.id)?.users, staleIds)
                return users === session?.users ? session : { ...session, users }
            }),
        },
    }
}

/** Ce qu'une liste dit : un `.map` sur une activité rend un nouveau tableau sans rien changer à la liste. */
const listContent = (list) =>
    Array.isArray(list) ? list.map((activity) => `${activity?.id}:${activity?.status}`).join() : ""

const sameList = (before, after) => before === after || listContent(before) === listContent(after)

const listsChanged = (before, after) =>
    !sameList(before?.todo_activities, after?.todo_activities) || !sameList(before?.activities, after?.activities)

/** La réponse est partie avant la dernière écriture de cette liste, revenue après. */
const staleFor = (previous, key, action) => {
    const writtenAt = previous.listsWrittenAt?.[key]
    return writtenAt != null && writtenAt > action.requestedAt && writtenAt <= action.respondedAt
}

/** Une copie qui n'a jamais porté ses listes n'a rien à reprendre. */
const carriesLists = (copy) => copy?.todo_activities !== undefined || copy?.activities !== undefined

const withListsOf = (copy, from) => ({ ...copy, todo_activities: from.todo_activities, activities: from.activities })

function changedStudentIds(previous, next) {
    const ids = []
    const previousGroups = copyGroups(previous)
    copyGroups(next).forEach(({ key, copies }) => {
        const previousCopies = previousGroups.find((group) => group.key === key)?.copies
        if (copies === previousCopies) return
        copies?.forEach((copy) => {
            if (listsChanged(copyIn(previousCopies, copy?.id), copy) && copy?.id != null) ids.push(copy.id)
        })
    })
    return ids
}

function withWrites(state, ids, at) {
    if (ids.length === 0) return state
    const listsWrittenAt = { ...state.listsWrittenAt }
    ids.forEach((id) => {
        listsWrittenAt[id] = Math.max(listsWrittenAt[id] ?? 0, at)
    })
    return { ...state, listsWrittenAt }
}

/**
 * L'état après une réponse de l'API (`requestedAt` et `respondedAt`, posés par `apiRequest`) : les listes
 * d'une lecture dépassée restent celles qu'on tenait, et l'heure de ce qui vient d'écrire une liste est
 * retenue pour les réponses à venir. `previous` : l'état avant la réponse, `next` : celui des réducteurs.
 */
export function settleLists(previous, next, action) {
    return settleAccountLists(previous, settleStudentLists(previous, next, action), action)
}

function settleStudentLists(previous, next, action) {
    if (action.respondedAt == null || action.requestedAt == null || previous.object === next.object) return next
    const read = readStudentIds(action)
    if (read == null) return withWrites(next, changedStudentIds(previous, next), action.respondedAt)
    const stale = read.filter((id) => staleFor(previous, id, action))
    const settled = stale.length > 0 ? restoreLists(previous, next, new Set(stale)) : next
    return withWrites(settled, read, action.requestedAt)
}

function settleAccountLists(previous, next, action) {
    if (action.respondedAt == null || action.requestedAt == null || previous.object === next.object) return next
    if (action.type !== ACCOUNT_READ) {
        return listsChanged(previous.object, next.object) ? withWrites(next, [ACCOUNT], action.respondedAt) : next
    }
    if (!Array.isArray(action.data?.todo_activities)) return next
    const kept = staleFor(previous, ACCOUNT, action) && carriesLists(previous.object)
    const settled = kept ? { ...next, object: withListsOf(next.object, previous.object) } : next
    return withWrites(settled, [ACCOUNT], action.requestedAt)
}
